import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { fetchOfficialCapability, fetchWorkspaceReports } from "@/lib/official";
import { SEVERITY_META, STATUS_META, priorityScore, type Severity } from "@/lib/reports";
import { CATEGORIES } from "@/lib/categories";
import { formatDistanceToNow } from "date-fns";
import { Building2, MapPin, CheckCircle2, MessageSquare, Users, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/workspace")({
  head: () => ({
    meta: [
      { title: "Xamseen — Authority Workspace" },
      { name: "description", content: "Government and agency workspace for triaging, assigning and resolving citizen reports with published proof." },
      { property: "og:title", content: "Authority Workspace — Xamseen" },
      { property: "og:description", content: "Triage, respond to and resolve citizen reports in your jurisdiction." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WorkspacePage,
});

type Row = Awaited<ReturnType<typeof fetchWorkspaceReports>>[number];

const QUEUES = [
  { key: "new", label: "Needs triage", match: (r: Row) => ["open", "under_review"].includes(r.status) && r.response_count === 0 },
  { key: "active", label: "In progress", match: (r: Row) => ["confirmed", "assigned", "in_progress", "discussing"].includes(r.status) },
  { key: "answered", label: "Responded", match: (r: Row) => r.response_count > 0 && r.status !== "resolved" },
  { key: "resolved", label: "Resolved", match: (r: Row) => ["resolved", "closed"].includes(r.status) },
] as const;

function WorkspacePage() {
  const { user } = useAuth();
  const [cap, setCap] = useState<Awaited<ReturnType<typeof fetchOfficialCapability>> | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [queue, setQueue] = useState<(typeof QUEUES)[number]["key"]>("new");

  useEffect(() => {
    if (!user) { setCap(null); setLoading(false); return; }
    fetchOfficialCapability(user.id).then(setCap);
  }, [user]);

  useEffect(() => {
    if (!cap?.canRespond) return;
    setLoading(true);
    fetchWorkspaceReports({ city: cap.city, country: cap.country, scope })
      .then((r) => setRows(r))
      .finally(() => setLoading(false));
  }, [cap, scope]);

  const queues = useMemo(() => {
    const m = new Map<string, Row[]>();
    QUEUES.forEach((q) => m.set(q.key, rows.filter(q.match)));
    return m;
  }, [rows]);

  const list = useMemo(
    () => (queues.get(queue) ?? []).slice().sort((a, b) => priorityScore(b) - priorityScore(a)),
    [queues, queue],
  );

  const resolvedRate = rows.length
    ? Math.round(((queues.get("resolved")?.length ?? 0) / rows.length) * 100)
    : 0;

  if (!user || (cap && !cap.canRespond)) {
    return (
      <AppShell>
        <Card className="mx-auto max-w-md p-8 text-center">
          <Building2 className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">Authority access required</p>
          <p className="mt-1 text-sm text-muted-foreground">
            The workspace is available to verified government, municipality and emergency accounts, plus platform moderators.
          </p>
          {!user && <Button asChild className="mt-4"><Link to="/auth">Sign in</Link></Button>}
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-6">
        <header>
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Building2 className="h-3.5 w-3.5 text-primary" /> Authority workspace
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            {cap?.agency ?? "Public authority"} desk
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Triage citizen reports, publish official responses and close the loop with resolution proof.
          </p>
        </header>

        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Open reports</p>
            <p className="font-display text-2xl font-semibold">{(queues.get("new")?.length ?? 0) + (queues.get("active")?.length ?? 0)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Responded</p>
            <p className="font-display text-2xl font-semibold">{queues.get("answered")?.length ?? 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Resolution rate</p>
            <p className="font-display text-2xl font-semibold">{resolvedRate}%</p>
          </Card>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(["mine", "all"] as const).map((s) => (
            <Button key={s} size="sm" variant={scope === s ? "default" : "outline"} onClick={() => setScope(s)}>
              {s === "mine" ? cap?.city || cap?.country || "My jurisdiction" : "All locations"}
            </Button>
          ))}
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-border">
          {QUEUES.map((q) => (
            <button
              key={q.key}
              onClick={() => setQueue(q.key)}
              className={cn(
                "whitespace-nowrap px-3 pb-2.5 pt-1 text-sm transition-colors",
                queue === q.key ? "border-b-2 border-primary font-semibold" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {q.label} <span className="text-xs text-muted-foreground">{queues.get(q.key)?.length ?? 0}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : list.length === 0 ? (
          <Card className="p-12 text-center">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-secondary" />
            <p className="font-medium">Nothing in this queue</p>
            <p className="mt-1 text-sm text-muted-foreground">New reports from your area will appear here automatically.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {list.map((r) => {
              const sev = SEVERITY_META[(r.severity ?? "info") as Severity];
              const cat = CATEGORIES[r.category as keyof typeof CATEGORIES];
              return (
                <Card key={r.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" className={cn(sev?.badge)}>{sev?.label} severity</Badge>
                        <Badge variant="secondary" className="font-normal">{cat?.label ?? r.category}</Badge>
                        <Badge variant="outline" style={{ borderColor: STATUS_META[r.status]?.color, color: STATUS_META[r.status]?.color }}>
                          {STATUS_META[r.status]?.label ?? r.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          priority {priorityScore(r)}
                        </span>
                      </div>
                      <Link to="/posts/$id" params={{ id: r.id }} className="font-medium hover:underline">
                        {r.title}
                      </Link>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.content}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {r.location_name && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.location_name}</span>}
                        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{r.verify_count} confirmed</span>
                        <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{r.comment_count}</span>
                        <span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                    {r.image_url && (
                      <img src={r.image_url} alt={r.title} loading="lazy" className="h-20 w-20 shrink-0 rounded-xl border border-border object-cover" />
                    )}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button asChild size="sm" variant="outline">
                      <Link to="/posts/$id" params={{ id: r.id }}>Respond & update status</Link>
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
